import type { TallyVoucherRow } from "@/utils/export-to-tally-excel";

import type { BankCreditTxn } from "./bank-statement-types";
import {
  assignVoucherNumbers,
  type AssignVoucherNumbersOptions,
  type NumberedBankCredit,
} from "./assign-voucher-numbers";

const VOUCHER_TYPE = "Receipt";

export type BankTallyMapping = {
  drLedger: string;
  crLedger: string;
  costCategory: string;
  costCentre: string;
};

export type BuildBankTallyVouchersInput = AssignVoucherNumbersOptions & {
  credits: BankCreditTxn[];
  mapping: BankTallyMapping;
};

function tallyTrim(value: unknown): string {
  return String(value ?? "").trim();
}

function costAllocationFields(
  categoryName: unknown,
  costCentre: unknown,
  amount: number
): Pick<TallyVoucherRow, "categoryName" | "costCentre" | "costAmount"> {
  const category = tallyTrim(categoryName);
  const centre = tallyTrim(costCentre);
  if (!category || !centre || amount <= 0) {
    return {};
  }
  return { categoryName: category, costCentre: centre, costAmount: amount };
}

/**
 * One bank credit → Cr then Dr lines (same shape as receipt Tally export).
 * Cost columns only on the Cr line.
 */
export function numberedCreditToVoucherRows(
  credit: NumberedBankCredit,
  mapping: BankTallyMapping
): TallyVoucherRow[] {
  const crLedger = tallyTrim(mapping.crLedger);
  const drLedger = tallyTrim(mapping.drLedger);
  const narration = tallyTrim(credit.description) || null;

  return [
    {
      voucherDate: credit.txnDate,
      voucherType: VOUCHER_TYPE,
      voucherNumber: credit.voucherNumber,
      ledgerName: crLedger,
      amount: credit.amount,
      drCr: "Cr",
      voucherNarration: narration,
      ledgerNarration: narration,
      ...costAllocationFields(mapping.costCategory, mapping.costCentre, credit.amount),
    },
    {
      voucherDate: null,
      voucherType: "",
      voucherNumber: "",
      ledgerName: drLedger,
      amount: credit.amount,
      drCr: "Dr",
    },
  ];
}

/**
 * Sort + number credits, then map each to Cr/Dr `TallyVoucherRow`s for `exportToTallyExcel`.
 */
export function buildBankTallyVouchers(input: BuildBankTallyVouchersInput): TallyVoucherRow[] {
  const numbered = assignVoucherNumbers(input.credits, {
    prefix: input.prefix,
    seed: input.seed,
  });
  return numbered.flatMap((credit) => numberedCreditToVoucherRows(credit, input.mapping));
}
